import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { costingService, inventoryVarianceService } from "~/services/costing";
import type { InventoryCountImportRow } from "~/services/costing";
import type {
  CostingItemKind,
  CostingMovementClass,
  CostingMovementInput,
  OpeningImportRow,
} from "~/types/costing";

const VARIANCE_KEY = "inventory-variance";
const COUNT_BY_PLANT_KEY = "inventory-count-by-plant";

const PERIODS_KEY = "costing_periods";
const MOVEMENTS_KEY = "costing_movements";
const REPORT_KEY = "costing_report";
const SALES_KEY = "costing_sales_report";

function useInvalidateCosting() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [PERIODS_KEY] });
    queryClient.invalidateQueries({ queryKey: [MOVEMENTS_KEY] });
    queryClient.invalidateQueries({ queryKey: [REPORT_KEY] });
    queryClient.invalidateQueries({ queryKey: [SALES_KEY] });
  };
}

// ---------- Queries ----------

export function useCostingPeriods() {
  return useQuery({
    queryKey: [PERIODS_KEY],
    queryFn: () => costingService.getPeriods(),
    staleTime: 60 * 1000,
  });
}

export function useCostingMovements(
  periodId: string | string[] | undefined,
  filters?: {
    itemKind?: CostingItemKind;
    movementClass?: CostingMovementClass | CostingMovementClass[];
  }
) {
  return useQuery({
    queryKey: [MOVEMENTS_KEY, periodId, filters],
    queryFn: () => costingService.getMovements(periodId!, filters),
    enabled: Array.isArray(periodId) ? periodId.length > 0 : !!periodId,
  });
}

export function useCostingMovementReport(
  period: string | undefined,
  itemKind?: CostingItemKind | CostingItemKind[]
) {
  return useQuery({
    queryKey: [REPORT_KEY, period, itemKind],
    queryFn: () => costingService.getMovementReport(period!, itemKind),
    enabled: !!period,
  });
}

/** Daily moving-average waterfall for a finished good (drill-down). */
export function useCostingMovementDailyFg(
  period: string | undefined,
  productId: string | null
) {
  return useQuery({
    queryKey: [REPORT_KEY, "daily_fg", period, productId],
    queryFn: () => costingService.getMovementDailyFg(period!, productId!),
    enabled: !!period && !!productId,
  });
}

/** Daily moving-average waterfall for an ingredient (drill-down). */
export function useCostingMovementDailyMp(
  period: string | undefined,
  itemId: string | null
) {
  return useQuery({
    queryKey: [REPORT_KEY, "daily_mp", period, itemId],
    queryFn: () => costingService.getMovementDailyMp(period!, itemId!),
    enabled: !!period && !!itemId,
  });
}

/** Ingredient consumption attributed to each finished product (SKU) for a period. */
export function useConsumptionBySku(period: string | undefined) {
  return useQuery({
    queryKey: [REPORT_KEY, "consumption_by_sku", period],
    queryFn: () => costingService.getConsumptionBySku(period!),
    enabled: !!period,
  });
}

/** Consolidated "Variación Real vs Teórico" for a month-end date (also used as the count read). */
export function useInventoryVariance(countDate: string | undefined) {
  return useQuery({
    queryKey: [VARIANCE_KEY, countDate],
    queryFn: () => inventoryVarianceService.getVariance(countDate!),
    enabled: !!countDate,
  });
}

/** Per-plant (Planta) breakdown of the month-end count for the "Por planta" view. */
export function useInventoryCountByPlant(countDate: string | undefined) {
  return useQuery({
    queryKey: [COUNT_BY_PLANT_KEY, countDate],
    queryFn: () => inventoryVarianceService.getCountByPlant(countDate!),
    enabled: !!countDate,
  });
}

// ---------- Mutations ----------

export function useEnsureCostingPeriod() {
  const invalidate = useInvalidateCosting();
  return useMutation({
    mutationFn: (period: string) => costingService.getOrCreatePeriod(period),
    onSuccess: invalidate,
  });
}

export function useCreateCostingMovement() {
  const invalidate = useInvalidateCosting();
  return useMutation({
    mutationFn: (input: CostingMovementInput) =>
      costingService.createMovement(input),
    onSuccess: invalidate,
  });
}

export function useUpdateCostingMovement() {
  const invalidate = useInvalidateCosting();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: Partial<CostingMovementInput>;
    }) => costingService.updateMovement(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteCostingMovement() {
  const invalidate = useInvalidateCosting();
  return useMutation({
    mutationFn: (id: string) => costingService.deleteMovement(id),
    onSuccess: invalidate,
  });
}

export function useImportOpeningInventory() {
  const invalidate = useInvalidateCosting();
  return useMutation({
    mutationFn: ({
      period,
      rows,
    }: {
      period: string;
      rows: OpeningImportRow[];
    }) => costingService.importOpening(period, rows),
    onSuccess: invalidate,
  });
}

/** Upload a Softland month-end physical count into inventory_physical_counts (feeds Variación + anchor). */
export function useImportInventoryCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      countDate: string;
      plant?: string;
      rows: InventoryCountImportRow[];
    }) => inventoryVarianceService.importCount(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VARIANCE_KEY] });
      queryClient.invalidateQueries({ queryKey: [COUNT_BY_PLANT_KEY] });
      queryClient.invalidateQueries({ queryKey: [REPORT_KEY] });
      queryClient.invalidateQueries({ queryKey: [MOVEMENTS_KEY] });
    },
  });
}

export function useCostingSyncAll() {
  const invalidate = useInvalidateCosting();
  return useMutation({
    mutationFn: (period: string) => costingService.syncAll(period),
    onSuccess: invalidate,
  });
}

export function useCostingRecompute() {
  const invalidate = useInvalidateCosting();
  return useMutation({
    mutationFn: (period: string) => costingService.recomputeWac(period),
    onSuccess: invalidate,
  });
}

export function useCostingClosePeriod() {
  const invalidate = useInvalidateCosting();
  return useMutation({
    mutationFn: (period: string) => costingService.closePeriod(period),
    onSuccess: invalidate,
  });
}
